import type { FieldMappingRule, NormalizationConfig } from '../../../types/pipelineConfig';

export interface TargetFieldOption {
  value: string;
  label: string;
  group: string;
}

export const TARGET_FIELD_OPTIONS: TargetFieldOption[] = [
  { value: 'title', label: 'Title', group: 'Core' },
  { value: 'description', label: 'Description', group: 'Core' },
  { value: 'htmlContent', label: 'HTML Content', group: 'Core' },
  { value: 'externalAlertId', label: 'External Alert ID', group: 'Core' },
  { value: 'externalReferenceId', label: 'External Reference ID', group: 'Core' },
  { value: 'monitorCondition', label: 'Monitor Condition', group: 'Core' },
  { value: 'condition', label: 'Condition', group: 'Core' },
  { value: 'impact', label: 'Impact', group: 'Classification' },
  { value: 'urgency', label: 'Urgency', group: 'Classification' },
  { value: 'priority', label: 'Priority', group: 'Classification' },
  { value: 'source', label: 'Source', group: 'Resource' },
  { value: 'resourceId', label: 'Resource ID', group: 'Resource' },
  { value: 'resourceIds', label: 'Resource IDs', group: 'Resource' },
  { value: 'resourceName', label: 'Resource Name', group: 'Resource' },
  { value: 'resourceNames', label: 'Resource Names', group: 'Resource' },
  { value: 'assignedTeamId', label: 'Assigned Team', group: 'Assignment' },
  { value: 'callerText', label: 'Caller Text', group: 'Assignment' },
  { value: 'isMajorIncident', label: 'Major Incident', group: 'Flags' },
];

function getRules(config?: NormalizationConfig | null): FieldMappingRule[] {
  if (!config) {
    return [];
  }

  const defaultRules = config.defaultNormalization?.rules ?? [];
  const conditionalRules = (config.conditionalNormalizations ?? []).flatMap(
    (conditional) => conditional.profile.rules,
  );

  return [...defaultRules, ...conditionalRules];
}

function toLabel(fieldName: string): string {
  return fieldName
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_.-]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function getNormalizedTargetFieldOptions(
  config?: NormalizationConfig | null,
): TargetFieldOption[] {
  const options = new Map<string, TargetFieldOption>();

  for (const option of TARGET_FIELD_OPTIONS) {
    options.set(option.value, option);
  }

  for (const rule of getRules(config)) {
    const targetField = rule.targetField.trim();
    if (!targetField || options.has(targetField)) {
      continue;
    }

    options.set(targetField, {
      value: targetField,
      label: toLabel(targetField),
      group: 'Custom',
    });
  }

  return Array.from(options.values());
}

export function getNormalizedTemplateTokenFields(
  config?: NormalizationConfig | null,
): string[] {
  return getNormalizedTargetFieldOptions(config).map((option) => `normalized.${option.value}`);
}
